import { CHECKOUT_TYPE } from "@/components/Checkout/constants.checkout";
import { CHECKOUT_MUTATION_KEY } from "@/components/Checkout/hooks";
import useUserPlanData from "@/components/Root/hooks/useUserPlanData";
import AppHelmet from "@/components/shared/AppHelmet/AppHelmet";
import useAppQueryParam from "@/hooks/useAppQueryParam";
import { useImageProcessDataQuery } from "@/hooks/useImageProcessDataQuery";
import { RouteErrorBoundary } from "@/lib/router";
import { createTrackerConfig } from "@/tracking/createTrackerConfig";
import eventTrackingHandler from "@/tracking/eventTrackingHandler";
import { useIsMutating } from "@tanstack/react-query";
import { DashboardContent } from "./components/DashboardContent";
import { DashboardSkeleton } from "./components/DashboardSkeleton";
import { BASE_DASHB_EVENT_PROPS } from "./events.dashboard";
import { useCheckoutDataQuery } from "@/components/Root/hooks/useCheckoutDataQuery";
import { DashboardService } from "./service.dashboard";
import { ProcessingOverlay } from "@/components/ui/Loader/ProcessingOverlay";

export const DashboardMain = () => {
  const { getQueryParam } = useAppQueryParam();

  DashboardService.init({
    getQueryParam,
    track: createTrackerConfig(eventTrackingHandler, {
      types: ["posthog", "customerio"],
      defaultProperties: BASE_DASHB_EVENT_PROPS,
    }),
  });

  const {
    data: userPlanData,
    isLoading: userPlanDataLoading,
    isFetching: userPlanDataFetching,
    isError: userPlanDataError,
  } = useUserPlanData();

  const userCheckout = useCheckoutDataQuery();
  useImageProcessDataQuery();

  const {
    data: userCheckoutData,
    isLoading: userCheckoutDataLoading,
    isError: userCheckoutDataError,
  } = userCheckout;

  const isLoading =
    userCheckoutDataLoading || (userPlanDataLoading && userPlanDataFetching);

  const pageError =
    userCheckoutDataError ||
    !userCheckoutData?.addOnCards ||
    !userCheckoutData?.addOnRules;

  // a parked `?intent=buy` selection resuming, fired by usePendingBuySelection
  const isPurchasing =
    useIsMutating({
      mutationKey: [CHECKOUT_MUTATION_KEY, CHECKOUT_TYPE.PURCHASE],
    }) > 0;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (pageError) {
    return <RouteErrorBoundary />;
  }

  return (
    <>
      <AppHelmet
        titleText="Manage Your Aftershoot Account - Transform your photography workflow
          with AI assisted Culling & Editing"
        metaDescription="Sign up for a 30-day free trial with Aftershoot culling and editing software. No credit card details required!"
      />

      {isPurchasing && (
        <ProcessingOverlay
          label={
            DashboardService.Content.dashboardPage.overlay.redirectingToCheckout
          }
        />
      )}

      <DashboardContent
        userPlanData={userPlanData}
        userPlanDataLoading={userPlanDataLoading}
        planCards={userCheckoutData?.planCards}
        addOnCards={userCheckoutData?.addOnCards}
        addOnRules={userCheckoutData?.addOnRules}
      />
    </>
  );
};
